/**
 * Global Timeline Component
 * 
 * Displays aggregated timeline data from all sites in various formats:
 * - Chronological timeline view
 * - Era-based grouping
 * - Culture-based grouping
 * - Regional distribution
 * - Interactive filtering and exploration
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  ButtonGroup,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
  useColorModeValue,
  Flex,
  Wrap,
  WrapItem,
  Progress,
  Divider
} from '@chakra-ui/react';
import {
  MagnifyingGlass,
  Clock,
  Globe,
  Buildings,
  ChartBar,
  Calendar,
  MapPin,
  Users,
  Sparkle
} from '@phosphor-icons/react';
import { useTimelineData } from '../../hooks/useTimelineData';
import { displayEra } from '../../utils/eraUtils';

const GlobalTimeline = () => {
  const {
    timelineData,
    loading,
    error,
    refreshTimelines,
    getStats,
    filterData,
    getAvailableEras,
    getAvailableCultures,
    getAvailableRegions,
    isLoaded,
    hasError,
    isEmpty,
    lastUpdated
  } = useTimelineData();

  // View state
  const [viewMode, setViewMode] = useState('chronological'); // chronological, era, culture, region, stats
  const [filters, setFilters] = useState({
    era: '',
    culture: '',
    region: '',
    country: '',
    searchTerm: '',
    mainPeriodsOnly: false
  });

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const accentColor = useColorModeValue('gold.500', 'gold.300');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  // Computed data
  const stats = useMemo(() => getStats(), [getStats]);
  const filteredData = useMemo(() => {
    if (!timelineData) return null;
    
    let currentFilters = { ...filters };
    
    // Add search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const searchFilteredPeriods = timelineData.allPeriods.filter(period =>
        period.siteName.toLowerCase().includes(searchLower) ||
        period.culture.toLowerCase().includes(searchLower) ||
        period.siteLocation.region.toLowerCase().includes(searchLower) ||
        period.siteLocation.country.toLowerCase().includes(searchLower) ||
        period.periodName.toLowerCase().includes(searchLower)
      );
      
      return {
        ...timelineData,
        allPeriods: searchFilteredPeriods,
        filteredCount: searchFilteredPeriods.length
      };
    }
    
    return filterData(currentFilters);
  }, [timelineData, filters, filterData]);

  const availableEras = useMemo(() => getAvailableEras(), [getAvailableEras]);
  const availableCultures = useMemo(() => getAvailableCultures(), [getAvailableCultures]);
  const availableRegions = useMemo(() => getAvailableRegions(), [getAvailableRegions]);

  // Event handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      era: '',
      culture: '',
      region: '',
      country: '',
      searchTerm: '',
      mainPeriodsOnly: false
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== false);

  // Loading state
  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color={accentColor} />
        <Text mt={4} color={mutedColor}>Loading timeline data from all sites...</Text>
      </Box>
    );
  }

  // Error state
  if (hasError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>Failed to load timeline data</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
          <Button mt={2} size="sm" onClick={() => refreshTimelines()}>
            Try Again
          </Button>
        </Box>
      </Alert>
    );
  }

  // Empty state
  if (isEmpty) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>No timeline data found</AlertTitle>
          <AlertDescription>
            No sites with construction period information were found in the database.
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return (
    <Box p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2} color={textColor}>
            Global Megalithic Timeline
          </Heading>
          <Text color={mutedColor}>
            Comprehensive timeline data from {stats?.totalSites} sites worldwide
            {lastUpdated && (
              <Text as="span" fontSize="sm" ml={2}>
                • Last updated: {lastUpdated.toLocaleDateString()}
              </Text>
            )}
          </Text>
        </Box>

        {/* Statistics Overview */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat>
            <StatLabel>Total Sites</StatLabel>
            <StatNumber>{stats?.totalSites || 0}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Sites with Timelines</StatLabel>
            <StatNumber>{stats?.sitesWithTimelines || 0}</StatNumber>
            <StatHelpText>{stats?.coveragePercentage || 0}% coverage</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Construction Periods</StatLabel>
            <StatNumber>{stats?.totalPeriods || 0}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>Timeline Range</StatLabel>
            <StatNumber fontSize="sm">
              {stats?.timelineRange?.earliest ? displayEra(stats.timelineRange.earliest) : 'Unknown'}
            </StatNumber>
            <StatHelpText>
              to {stats?.timelineRange?.latest ? displayEra(stats.timelineRange.latest) : 'Unknown'}
            </StatHelpText>
          </Stat>
        </SimpleGrid>

        {/* Controls */}
        <Card>
          <CardHeader pb={2}>
            <Heading size="md">View & Filter Options</Heading>
          </CardHeader>
          <CardBody pt={2}>
            <VStack spacing={4} align="stretch">
              {/* View Mode Selection */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>View Mode</Text>
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    leftIcon={<Clock size={16} />}
                    isActive={viewMode === 'chronological'}
                    onClick={() => setViewMode('chronological')}
                  >
                    Chronological
                  </Button>
                  <Button
                    leftIcon={<Calendar size={16} />}
                    isActive={viewMode === 'era'}
                    onClick={() => setViewMode('era')}
                  >
                    By Era
                  </Button>
                  <Button
                    leftIcon={<Users size={16} />}
                    isActive={viewMode === 'culture'}
                    onClick={() => setViewMode('culture')}
                  >
                    By Culture
                  </Button>
                  <Button
                    leftIcon={<MapPin size={16} />}
                    isActive={viewMode === 'region'}
                    onClick={() => setViewMode('region')}
                  >
                    By Region
                  </Button>
                  <Button
                    leftIcon={<ChartBar size={16} />}
                    isActive={viewMode === 'stats'}
                    onClick={() => setViewMode('stats')}
                  >
                    Statistics
                  </Button>
                </ButtonGroup>
              </Box>

              {/* Filters */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>Search</Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <MagnifyingGlass size={16} color={mutedColor} />
                    </InputLeftElement>
                    <Input
                      placeholder="Search sites, cultures, regions..."
                      value={filters.searchTerm}
                      onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    />
                  </InputGroup>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>Era</Text>
                  <Select
                    placeholder="All eras"
                    value={filters.era}
                    onChange={(e) => handleFilterChange('era', e.target.value)}
                  >
                    {availableEras.map(era => (
                      <option key={era} value={era}>{era}</option>
                    ))}
                  </Select>
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>Culture</Text>
                  <Select
                    placeholder="All cultures"
                    value={filters.culture}
                    onChange={(e) => handleFilterChange('culture', e.target.value)}
                  >
                    {availableCultures.map(culture => (
                      <option key={culture} value={culture}>{culture}</option>
                    ))}
                  </Select>
                </Box>
              </SimpleGrid>

              {/* Filter Actions */}
              <Flex justify="space-between" align="center">
                <Box>
                  {hasActiveFilters && (
                    <Button size="sm" variant="ghost" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </Box>
                <Box>
                  {filteredData && (
                    <Text fontSize="sm" color={mutedColor}>
                      Showing {filteredData.filteredCount || filteredData.allPeriods.length} of {timelineData.allPeriods.length} periods
                    </Text>
                  )}
                </Box>
              </Flex>
            </VStack>
          </CardBody>
        </Card>

        {/* Content based on view mode */}
        {viewMode === 'chronological' && (
          <ChronologicalView data={filteredData} />
        )}
        
        {viewMode === 'era' && (
          <EraView data={filteredData} />
        )}
        
        {viewMode === 'culture' && (
          <CultureView data={filteredData} />
        )}
        
        {viewMode === 'region' && (
          <RegionView data={filteredData} />
        )}
        
        {viewMode === 'stats' && (
          <StatisticsView stats={stats} data={filteredData} />
        )}
      </VStack>
    </Box>
  );
};

// Chronological View Component
const ChronologicalView = ({ data }) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const accentColor = useColorModeValue('gold.500', 'gold.300');

  if (!data || !data.allPeriods.length) {
    return (
      <Alert status="info">
        <AlertIcon />
        No periods match the current filters.
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <Heading size="md">Chronological Timeline</Heading>
        <Text color={mutedColor} fontSize="sm">
          Construction periods ordered by start date
        </Text>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {data.allPeriods.map((period, index) => (
            <PeriodCard key={`${period.siteId}-${period.periodIndex}`} period={period} />
          ))}
        </VStack>
      </CardBody>
    </Card>
  );
};

// Era View Component
const EraView = ({ data }) => {
  if (!data) return null;

  const periodsByEra = data.periodsByEra || {};

  return (
    <VStack spacing={4} align="stretch">
      {Object.entries(periodsByEra).map(([era, periods]) => (
        <Card key={era}>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">{era}</Heading>
              <Badge colorScheme="blue">{periods.length} periods</Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {periods.map((period, index) => (
                <PeriodCard key={`${period.siteId}-${period.periodIndex}`} period={period} compact />
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      ))}
    </VStack>
  );
};

// Culture View Component
const CultureView = ({ data }) => {
  if (!data) return null;

  const periodsByCulture = data.periodsByCulture || {};

  return (
    <Accordion allowMultiple>
      {Object.entries(periodsByCulture).map(([culture, periods]) => (
        <AccordionItem key={culture}>
          <h2>
            <AccordionButton>
              <Box flex="1" textAlign="left">
                <HStack>
                  <Text fontWeight="medium">{culture}</Text>
                  <Badge colorScheme="green">{periods.length} periods</Badge>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {periods.map((period, index) => (
                <PeriodCard key={`${period.siteId}-${period.periodIndex}`} period={period} compact />
              ))}
            </SimpleGrid>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

// Region View Component
const RegionView = ({ data }) => {
  if (!data) return null;

  const periodsByRegion = data.periodsByRegion || {};

  return (
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
      {Object.entries(periodsByRegion).map(([region, periods]) => (
        <Card key={region}>
          <CardHeader>
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Heading size="sm">{region}</Heading>
                <Text fontSize="xs" color="gray.500">
                  {periods.length} construction periods
                </Text>
              </VStack>
              <Badge colorScheme="purple">{periods.length}</Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              {periods.slice(0, 5).map((period, index) => (
                <PeriodCard key={`${period.siteId}-${period.periodIndex}`} period={period} compact minimal />
              ))}
              {periods.length > 5 && (
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  ... and {periods.length - 5} more
                </Text>
              )}
            </VStack>
          </CardBody>
        </Card>
      ))}
    </SimpleGrid>
  );
};

// Statistics View Component
const StatisticsView = ({ stats, data }) => {
  if (!stats) return null;

  return (
    <VStack spacing={6} align="stretch">
      {/* Era Distribution */}
      <Card>
        <CardHeader>
          <Heading size="md">Distribution by Era</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={3} align="stretch">
            {stats.eraDistribution.map(({ era, count }) => (
              <Box key={era}>
                <Flex justify="space-between" mb={1}>
                  <Text fontWeight="medium">{era}</Text>
                  <Text color="gray.500">{count} periods</Text>
                </Flex>
                <Progress 
                  value={(count / stats.totalPeriods) * 100} 
                  colorScheme="blue" 
                  size="sm" 
                />
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>

      {/* Culture Distribution */}
      <Card>
        <CardHeader>
          <Heading size="md">Top Cultures by Period Count</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={3} align="stretch">
            {stats.cultureDistribution.slice(0, 10).map(({ culture, count }) => (
              <Box key={culture}>
                <Flex justify="space-between" mb={1}>
                  <Text fontWeight="medium">{culture}</Text>
                  <Text color="gray.500">{count} periods</Text>
                </Flex>
                <Progress 
                  value={(count / stats.totalPeriods) * 100} 
                  colorScheme="green" 
                  size="sm" 
                />
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>

      {/* Region Distribution */}
      <Card>
        <CardHeader>
          <Heading size="md">Top Regions by Period Count</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={3} align="stretch">
            {stats.regionDistribution.slice(0, 10).map(({ region, count }) => (
              <Box key={region}>
                <Flex justify="space-between" mb={1}>
                  <Text fontWeight="medium">{region}</Text>
                  <Text color="gray.500">{count} periods</Text>
                </Flex>
                <Progress 
                  value={(count / stats.totalPeriods) * 100} 
                  colorScheme="purple" 
                  size="sm" 
                />
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

// Period Card Component
const PeriodCard = ({ period, compact = false, minimal = false }) => {
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const accentColor = useColorModeValue('gold.500', 'gold.300');

  if (minimal) {
    return (
      <Box p={3} bg={bgColor} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <VStack spacing={1} align="start">
          <Text fontWeight="medium" fontSize="sm">{period.siteName}</Text>
          <Text fontSize="xs" color={mutedColor}>
            {displayEra(period.startEra)} - {displayEra(period.endEra)}
          </Text>
          <Text fontSize="xs" color={mutedColor}>{period.culture}</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box 
      p={compact ? 4 : 6} 
      bg={bgColor} 
      borderRadius="md" 
      borderWidth="1px" 
      borderColor={borderColor}
      borderLeftWidth="4px"
      borderLeftColor={period.isMainPeriod ? accentColor : borderColor}
    >
      <VStack spacing={3} align="start">
        <HStack justify="space-between" w="full">
          <VStack align="start" spacing={1}>
            <Heading size="sm" color={textColor}>{period.siteName}</Heading>
            <Text fontSize="sm" color={mutedColor}>{period.periodName}</Text>
          </VStack>
          {period.isMainPeriod && (
            <Tooltip label="Main construction period">
              <Badge colorScheme="yellow" leftIcon={<Sparkle size={12} />}>
                Main
              </Badge>
            </Tooltip>
          )}
        </HStack>

        <HStack spacing={4} wrap="wrap">
          <VStack align="start" spacing={1}>
            <Text fontSize="xs" fontWeight="medium" color={mutedColor}>PERIOD</Text>
            <Text fontSize="sm">
              {displayEra(period.startEra)} - {displayEra(period.endEra)}
            </Text>
          </VStack>
          
          <VStack align="start" spacing={1}>
            <Text fontSize="xs" fontWeight="medium" color={mutedColor}>CULTURE</Text>
            <Text fontSize="sm">{period.culture}</Text>
          </VStack>
          
          <VStack align="start" spacing={1}>
            <Text fontSize="xs" fontWeight="medium" color={mutedColor}>LOCATION</Text>
            <Text fontSize="sm">{period.siteLocation.region}, {period.siteLocation.country}</Text>
          </VStack>
        </HStack>

        {period.features && period.features.length > 0 && (
          <Box>
            <Text fontSize="xs" fontWeight="medium" color={mutedColor} mb={2}>FEATURES</Text>
            <Wrap spacing={1}>
              {period.features.slice(0, compact ? 3 : 6).map((feature, index) => (
                <WrapItem key={index}>
                  <Badge size="sm" variant="outline">{feature}</Badge>
                </WrapItem>
              ))}
              {period.features.length > (compact ? 3 : 6) && (
                <WrapItem>
                  <Badge size="sm" variant="outline">+{period.features.length - (compact ? 3 : 6)} more</Badge>
                </WrapItem>
              )}
            </Wrap>
          </Box>
        )}

        {!compact && period.details && (
          <Box>
            <Text fontSize="xs" fontWeight="medium" color={mutedColor} mb={1}>DETAILS</Text>
            <Text fontSize="sm" noOfLines={3}>{period.details}</Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default GlobalTimeline;